import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router";
import { ListChecksIcon, PlusIcon } from "@phosphor-icons/react";
import { useShallow } from "zustand/shallow";
import clsx from "clsx";

import { PageDataPlaceholder } from "@/shared/components/ui/PageDataPlaceholder";
import { TodosList } from "@/features/todos/components/TodosList";
import { TodoDatePeriod } from "@/features/todos/types";
import { useBoundStore } from "@/app/store";
import { useMenuItems } from "@/shared/hooks/useMenuItems";
import { useOpenState } from "@/shared/hooks/useOpenState";
import { TodosDatePeriodFilter } from "@/features/todos/components/TodosDatePeriodFilter";
import { TodoRow } from "@/services/api/fetchers/todos";
import { dateValueToISOString, isoStringToDateValue } from "@/shared/utils/date";
import { TODO_DATE_PERIOD_FILTERS } from "@/features/todos/constants";
import { getFilteredTodos, calculateFractionalPosition } from "@/features/todos/utils/general";
import { getEmptyStateMessage } from "@/features/todos/utils/empty-state";
import { TodoFormValues, ManageTodo } from "../../../features/todos/components/ManageTodo";
import { usePageLayout } from "@/app/hooks/usePageLayout";

export function TodosRoute() {
  const [editingTodo, setEditingTodo] = useState<TodoRow | null>(null);
  const { isOpen: isOpenManageTodo, handleOpenChange: handleOpenChangeManageTodo } = useOpenState({ id: "todo" });
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const todosSettings = useBoundStore(useShallow((state) => state.settings.todos));
  const updateSettings = useBoundStore(useShallow((state) => state.settings.updateSettings));
  const { todos, completed, addTodo, removeTodo, updateTodo } = useBoundStore(
    useShallow((state) => ({
      todos: state.todos.items,
      completed: state.todos.completed,
      addTodo: state.todos.addTodo,
      removeTodo: state.todos.deleteTodo,
      updateTodo: state.todos.updateTodo,
      fetchCompletedTodos: state.todos.fetchCompletedTodos,
    }))
  );
  const menuItems = useMenuItems("todos");
  const isViewingDone = todosSettings.filters.period === "done";
  const displayTodos = isViewingDone ? completed : todos;
  const filteredTodos = getFilteredTodos(displayTodos, todosSettings.filters.period);
  const navigate = useNavigate();
  const isMobile = deviceType === "mobile";

  useEffect(() => {
    if (isViewingDone) {
      useBoundStore.getState().todos.fetchCompletedTodos();
    }
  }, [isViewingDone]);

  const handleFilterChange = useCallback(
    (filter: TodoDatePeriod) => {
      if (userId) {
        updateSettings(userId, { todos: { ...todosSettings, filters: { ...todosSettings.filters, period: filter } } });
      }
      navigate(-1);
    },
    [userId, updateSettings, todosSettings, navigate]
  );

  function handleTodoPress(id: number) {
    const todo = todos.find((t) => t.id === id) || completed.find((t) => t.id === id);
    if (todo) {
      setEditingTodo(todo);
      handleOpenChangeManageTodo(true);
    }
  }

  async function handleAddTodo(formValue: TodoFormValues) {
    // With fractional ordering, calculate position between first item and 0
    const rootTodos = todos.filter((t) => t.parent_id === null).sort((a, b) => a.position - b.position);
    const newPosition = calculateFractionalPosition(null, rootTodos.length > 0 ? rootTodos[0].position : null);

    addTodo({
      due_date: formValue.dueDate ? dateValueToISOString(formValue.dueDate) : null,
      position: newPosition,
      priority: formValue.priority,
      text: formValue.name,
      user_id: userId ?? "",
    });
    handleOpenChangeManageTodo(false);
  }

  async function handleEditTodo(formValue: TodoFormValues) {
    if (editingTodo) {
      updateTodo(editingTodo.id, {
        due_date: formValue.dueDate ? dateValueToISOString(formValue.dueDate) : null,
        text: formValue.name,
        priority: formValue.priority,
      });
    }
    setEditingTodo(null);
    handleOpenChangeManageTodo(false);
  }

  function handleRemove(id?: number) {
    if (todos.length > 0 && id) {
      removeTodo(id);
      setEditingTodo(null);

      // The drawer is displayed only on mobile devices
      if (deviceType === "mobile") {
        handleOpenChangeManageTodo(false);
      }
    }
  }

  function handleToggle(id: number) {
    const todo = todos.find((t) => t.id === id) || completed.find((t) => t.id === id);
    if (todo) {
      updateTodo(id, {
        done: !todo.done,
        done_at: !todo.done ? new Date().toISOString() : null,
      });
    }
  }

  const handleAdd = useCallback(() => {
    setEditingTodo(null);
    handleOpenChangeManageTodo(true);
  }, [handleOpenChangeManageTodo]);

  // Layout config with memoized handlers - usePageLayout is optimal here
  // Handlers are already wrapped in useCallback and don't access rapidly-changing state
  const pageLayoutConfig = useMemo(
    () => ({
      showBottomNavigation: true,
      fab: { icon: <PlusIcon />, onPress: handleAdd },
      navbar: {
        hideBack: true,
        menuItems,
        title:
          Object.values(TODO_DATE_PERIOD_FILTERS).find((f) => f.value === todosSettings.filters.period)?.label ||
          "Upcoming",
        actions: [<TodosDatePeriodFilter key="filter" onFilterChange={handleFilterChange} />],
      },
    }),
    [handleAdd, menuItems, todosSettings.filters.period, handleFilterChange]
  );

  usePageLayout(pageLayoutConfig);

  return (
    <>
      {filteredTodos.length ? (
        <div className="mx-auto max-w-2xl">
          {/* {isViewingDone ? ( */}
          <TodosList
            className={clsx({ "px-4": !isMobile })}
            groupBy={todosSettings.filters.period === "done" ? "doneDate" : undefined}
            items={filteredTodos
              .sort((a, b) => {
                // When viewing done items, sort by doneAt (most recently done first)
                if (isViewingDone) {
                  const aDoneAt = a.done_at ? new Date(a.done_at) : null;
                  const bDoneAt = b.done_at ? new Date(b.done_at) : null;

                  if (aDoneAt && bDoneAt) {
                    return bDoneAt.getTime() - aDoneAt.getTime();
                  }
                  if (aDoneAt) return -1;
                  if (bDoneAt) return 1;
                  return 0;
                }

                // Otherwise sort by priority first (higher priority first)
                const priorityDiff = (a.priority ?? 0) - (b.priority ?? 0);
                if (priorityDiff !== 0) return priorityDiff;

                // Then sort by due date (earlier due date first)
                const aDueDate = a.due_date ? new Date(a.due_date) : null;
                const bDueDate = b.due_date ? new Date(b.due_date) : null;

                if (aDueDate && bDueDate) {
                  return aDueDate.getTime() - bDueDate.getTime();
                }
                if (aDueDate) return -1;
                if (bDueDate) return 1;
                return 0;
              })
              .map((todo) => ({
                id: todo.id,
                text: todo.text,
                done: todo.done,
                doneAt: todo.done_at,
                dueDate: todo.due_date ? new Date(todo.due_date) : null,
                priority: todo.priority,
                children: [],
                collapsed: false,
              }))}
            showDue={["upcoming", "week"].includes(todosSettings.filters.period)}
            onToggleDone={handleToggle}
            onTodoPress={handleTodoPress}
            onRemove={handleRemove}
          />
        </div>
      ) : (
        <>
          {(() => {
            const emptyState = getEmptyStateMessage(todosSettings.filters.period);
            return (
              <PageDataPlaceholder
                icon={<ListChecksIcon />}
                title={emptyState.title}
                description={emptyState.description}
              />
            );
          })()}
        </>
      )}
      <ManageTodo
        initialValues={
          editingTodo
            ? {
                name: editingTodo.text || "",
                priority: editingTodo.priority || 0,
                dueDate: editingTodo.due_date ? isoStringToDateValue(editingTodo.due_date) : null,
              }
            : undefined
        }
        isOpen={isOpenManageTodo}
        onDelete={() => handleRemove(editingTodo?.id)}
        onOpenChange={handleOpenChangeManageTodo}
        onSave={editingTodo ? handleEditTodo : handleAddTodo}
      />
    </>
  );
}
